import React, { ChangeEvent, useEffect, useState } from 'react'

import { Grid } from '@material-ui/core'

import Autocomplete from 'shared/components/Autocomplete'
import LoadingBackdrop from 'shared/components/LoadingBackdrop'
import SnackBar from 'shared/components/SnackBar'
import useDebounce from 'shared/hooks/useDebounce'
import { colors } from 'shared/styles/colors'
import { ICancelaViagem } from 'modules/lotes/domain/usecases/CancelaViagem'
import { IFinalizaViagem } from 'modules/lotes/domain/usecases/FinalizaViagem'
import { IValidateMotorista } from 'modules/lotes/domain/usecases/ValidateMotorista'
import { useDirecionaCarga } from 'modules/lotes/hooks/direcionaCarga'
import IMotorista from 'modules/motorista/domain/entities/IMotorista'
import { IFindByCpfNomeFrotistaId } from 'modules/motorista/domain/usecases/FindByCpfNomeFrotistaId'
import { useFrotista } from 'modules/user/hooks/useFrotista'

import ModalFinalizarCancelarViagem from '../components/ModalFinalizarCancelarViagem'
import LabelValue from '../components/SimpleLabelValue'
import { Container, ContainerAutocomplete } from './styles'

type TProps = {
  findByNome: IFindByCpfNomeFrotistaId
  validateMotorista: IValidateMotorista
  finalizaViagem: IFinalizaViagem
  cancelaViagem: ICancelaViagem
}
export enum EStatusMotorista {
  EMBARCADO = 'EMBARCADO',
  EM_TRANSITO = 'EM_TRANSITO',
}

const SelecionaMotorista: React.FC<TProps> = ({
  findByNome,
  validateMotorista,
  finalizaViagem,
  cancelaViagem,
}) => {
  const { frotistaId } = useFrotista()
  const [open, setOpen] = useState(false)
  const [messages, setMessages] = useState('')
  const [motoristas, setMotoristas] = useState<IMotorista[]>()
  const [motoristaInput, setMotoristaInput] = useState('')
  const [loading, setLoading] = useState(false)
  const { motorista, setMotorista } = useDirecionaCarga()
  const [openModal, setOpenModal] = useState(false)
  const [statusMotorista, setStatusMotorista] = useState<EStatusMotorista>()
  const [contatoFilial, setContatoFilial] = useState('')
  const [loadingBackdrop, setLoadingBackdrop] = useState(false)
  const [loteConjuntoId, setLoteConjuntoId] = useState(0)
  const [lotesConjunto, setLotesConjunto] = useState([])
  const [loteIndex, setLoteIndex] = useState(0)
  const [openAutocomplete, setOpenAutocomplete] = useState(false)

  const debouncedMotorista = useDebounce(motoristaInput, 1000)

  useEffect(() => {
    const init = async () => {
      if (debouncedMotorista && motoristaInput) {
        setLoading(true)
        try {
          const response = await findByNome.execute({
            page: 1,
            perPage: 20,
            nome: motoristaInput,
            list_status: ['VALID', 'MANUAL_APPROVAL'],
            ativo: true,
            frotista_id: frotistaId,
          })
          setLoading(false)
          setMotoristas(response.data)
        } catch (error) {
          setLoading(false)
          setOpen(true)
          setMessages(
            'Problemas ao realizar a consulta, tente novamente mais tarde',
          )
        }
      } else {
        setMotoristas([])
      }
    }

    init()
  }, [debouncedMotorista, findByNome, motoristaInput])

  const handleCloseModal = () => {
    setOpenModal(false)

    const totalLotes = lotesConjunto.length - 1

    if (loteIndex < totalLotes) {
      const newLoteIndex = loteIndex + 1

      setLoteConjuntoId(lotesConjunto[newLoteIndex])
      setLoteIndex(newLoteIndex)

      setTimeout(() => {
        setOpenModal(true)
      }, 500)
    }
  }

  const handleMotorista = (motorista: string) => {
    if (motorista) {
      setOpenAutocomplete(true)
    } else {
      setOpenAutocomplete(false)
    }

    setMotoristaInput(motorista)
  }

  const motoristaVinculadoEmViagem = (messages: string[]) => {
    return messages.find(
      msg =>
        msg.toLowerCase().includes('transito') ||
        msg.toLowerCase().includes('na fila') ||
        msg.toLowerCase().includes('confirmado') ||
        msg.toLowerCase().includes('carregando') ||
        msg.toLowerCase().includes('aguardando aprovacao'),
    )
  }

  const validateMotoristaCpf = (newValue: IMotorista) => {
    if (!newValue) return

    setOpenAutocomplete(false)

    const init = async () => {
      setLoadingBackdrop(true)
      setMotorista(newValue)
      const params = {
        cpf: newValue.cpf,
      }
      try {
        const response = await validateMotorista.execute(params)
        setLoadingBackdrop(false)
        if (response.isValid) {
          setMotorista(newValue)
          return
        }
        setContatoFilial(response.contatoFilial)

        if (
          response.messages.find(msg => msg.toLowerCase().includes('embarcado'))
        ) {
          setStatusMotorista(EStatusMotorista.EMBARCADO)
          setLoteConjuntoId(response.loteConjuntoId)
          setOpenModal(true)
        }
        if (motoristaVinculadoEmViagem(response.messages)) {
          setStatusMotorista(EStatusMotorista.EM_TRANSITO)
          setLoteConjuntoId(response.loteConjuntoId)
          setOpenModal(true)
        }

        if (response.lotes && response.lotes.length > 0) {
          setLotesConjunto(response.lotes)
          setStatusMotorista(EStatusMotorista.EM_TRANSITO)
          setLoteConjuntoId(Number(response.lotes[0]))
          setOpenModal(true)
        }
      } catch (error) {
        setLoadingBackdrop(false)
        setOpen(true)
        setMessages(
          'Problemas ao realizar a consulta, tente novamente mais tarde',
        )
      }
    }
    init()
  }

  useEffect(() => {
    if (motorista === null) {
      setMotoristaInput('')
    }
  }, [motorista])

  return (
    <Container data-testid="select-driver">
      <Grid container spacing={6} style={{ paddingLeft: 20, paddingRight: 20 }}>
        <Grid container>
          <Grid item xs={12} md={12}>
            <ContainerAutocomplete>
              <Autocomplete
                label="Buscar motorista"
                placeholder="Digite o nome para buscar"
                options={motoristas}
                inputValue={motoristaInput}
                onInputChange={(_, newInputValue) => {
                  handleMotorista(newInputValue)
                }}
                onChange={(
                  _event: ChangeEvent<HTMLInputElement>,
                  newValue: IMotorista,
                ) => {
                  validateMotoristaCpf(newValue)
                }}
                getOptionLabel={option => (option as IMotorista).nome}
                loading={loading}
                open={openAutocomplete}
              />
            </ContainerAutocomplete>
          </Grid>
        </Grid>

        {motorista?.nome && (
          <>
            <Grid
              container
              spacing={6}
              style={{ paddingBottom: 20, paddingTop: 50 }}
            >
              <Grid item md={4}>
                <Grid container>
                  <LabelValue title="Nome" value={motorista?.nome} />
                </Grid>
              </Grid>

              <Grid item md={4}>
                <Grid container>
                  <LabelValue title="CNH" value={motorista?.cnh} />
                </Grid>
              </Grid>
            </Grid>

            <Grid container>
              <Grid item md={4}>
                <Grid container>
                  <LabelValue
                    title="Vencimento CNH"
                    value={motorista?.vencimento_cnh?.toString()}
                  />
                </Grid>
              </Grid>
            </Grid>
          </>
        )}
      </Grid>

      <SnackBar
        open={open}
        message={messages}
        setOpen={setOpen}
        severity={'error'}
      />

      {loadingBackdrop && (
        <LoadingBackdrop open={loadingBackdrop} color={colors.green} />
      )}

      {openModal && (
        <ModalFinalizarCancelarViagem
          loteConjuntoId={loteConjuntoId}
          finalizaViagem={finalizaViagem}
          cancelaViagem={cancelaViagem}
          status={statusMotorista}
          contatoFilial={contatoFilial}
          openModal={openModal}
          setOpenModal={handleCloseModal}
        />
      )}
    </Container>
  )
}

export default SelecionaMotorista
